"use client";
/**
 * ATTACH ON THE CALL (2026-09-24; plan docs/superpowers/plans/2026-09-24-voice-attach.md,
 * mock of record mockups/voice-attach-2026-09-24.html). During an open line the person adds
 * files without switching to typing: the tray's attach seat opens the typing box's OWN picker,
 * the files ride the typing box's OWN compress and upload lane (useAttachmentHandler), and the
 * card under the ball shows them in the typing box's OWN strip. Once no row is still working,
 * the settled rows go to the line's attach door in the wire the typing box's converse sends
 * (toComposerAttachments), and they wait there for the next ask, spoken or typed.
 *
 * A row keeps its × until it is handed to the line; after that its chip stays until the server
 * says a turn took it (a state frame saying `staged: 0`, read as a level on every frame) or the
 * line ends. A refusal (the door's own words, or a pick the lane turned away) is the row's one
 * note under the ball. A pick that would take the files past the per-ask cap is refused before
 * anything uploads, in the door's own sentence.
 *
 * Offered only where the line's door said it takes files (`canAttach` on the session answer),
 * so with the server's switch off none of this is on screen. Per line, never global: every
 * open and every close starts the card empty.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { LIVE_ATTACH_MAX_FILES, LIVE_ATTACH_TOO_MANY } from "@bash/shared";
import { useAttachmentHandler } from "@/components/message-input";
import type { PendingAttachment } from "@/components/message-input/AttachmentHandler";
import { toComposerAttachments } from "@/components/composer/useComposerConverse";
import { rowsToAttachments } from "@/components/bash-home/usePillFiles";
import { LineAttachRefused, liveAttach } from "./liveClient";
import { useLiveStore } from "./liveStore";

/** How long a refusal holds the row under the ball: the closed note's four seconds. */
export const ATTACH_NOTE_MS = 4000;
/** The stage's own words for a refusal the server gave none for. */
export const ATTACH_FAILED = "I could not add those files. Try again.";

export interface LineAttach {
  /** The seat is offered: the line is open and its door said it takes files. */
  on: boolean;
  /** The files on the card, as the typing box's own rows. */
  rows: PendingAttachment[];
  /** Whether a row can still come off the card (not yet handed to the line). */
  removable(id: string): boolean;
  /** The tray's seat: the typing box's own picker. */
  pick(): void;
  /** A drop on the ball during the call: the same lane as the picker. */
  stage(files: File[]): void;
  remove(id: string): void;
  /** A refusal for the row under the ball, or null. */
  note: string | null;
}

const NONE: ReadonlySet<string> = new Set();

/** The typing box's own mapping from its rows (BashPill's send), with the
 *  image's name kept so the call can name what it was given. */
function wireOf(rows: PendingAttachment[]) {
  const { images, files } = rowsToAttachments(rows, { named: true });
  return toComposerAttachments(images, files) ?? [];
}

export function useLineAttach(): LineAttach {
  const lane = useAttachmentHandler();
  const rows = lane.pendingAttachments;
  const open = useLiveStore((s) => s.line.phase === "open");
  const on = useLiveStore(
    (s) => s.line.phase === "open" && s.handle?.canAttach === true
  );
  // The batch on its way to the door, and the rows the door already has.
  const [inFlight, setInFlight] = useState<PendingAttachment[] | null>(null);
  const [posted, setPosted] = useState<ReadonlySet<string>>(NONE);
  const [refusal, setRefusal] = useState<string | null>(null);

  // Every open and every close starts the card empty: the files belong to
  // the line they were given to, and the server drops them on its close.
  // Otherwise, once no row is still working, the settled rows nobody has sent
  // go to the door together, one batch at a time (never on the render that
  // empties the card: its rows are the ones being thrown away). Both are
  // adjusted during render, the stage's own pattern, so no paint shows the
  // last call's chips on the next one.
  const [prevOpen, setPrevOpen] = useState(open);
  const busy = rows.some((r) => r.status === "processing");
  const ready = rows.filter(
    (r) =>
      r.status === "complete" &&
      !posted.has(r.id) &&
      !inFlight?.some((f) => f.id === r.id)
  );
  if (prevOpen !== open) {
    setPrevOpen(open);
    setInFlight(null);
    setPosted(NONE);
    setRefusal(null);
    lane.resetAttachments();
  } else if (on && !busy && !inFlight && ready.length > 0) setInFlight(ready);

  // The lane's handlers and the rows the door has, for the callbacks below
  // (the lane's functions are not all stable).
  const laneRef = useRef(lane);
  const postedRef = useRef(posted);
  useEffect(() => {
    laneRef.current = lane;
    postedRef.current = posted;
  });
  // Whether a frame raised the count while a batch was on its way: the frame
  // and the door's answer ride different channels, so a turn can take the
  // batch before the answer lands, and then its 0 has already gone by.
  const flight = useRef({ sawWaiting: false });

  // A frame saying 0 is a turn taking the files: their chips go. Read as a
  // level on every frame, never only as a fall from above 0, so a lost frame
  // (a socket reconnect) cannot leave chips the line no longer holds.
  useEffect(
    () =>
      useLiveStore.subscribe((s, prev) => {
        if (s.staged > 0 && s.staged !== prev.staged)
          flight.current.sawWaiting = true;
        const frame = s.stagedFrames !== prev.stagedFrames;
        if (frame && s.staged === 0 && postedRef.current.size > 0) {
          for (const id of postedRef.current)
            laneRef.current.removeAttachment(id);
          setPosted(NONE);
        }
      }),
    []
  );

  useEffect(() => {
    if (!inFlight) return;
    const { sessionId } = useLiveStore.getState().line;
    const ids = inFlight.map((r) => r.id);
    const mine = () => {
      const { line } = useLiveStore.getState();
      return line.phase === "open" && line.sessionId === sessionId;
    };
    const drop = () => {
      for (const id of ids) laneRef.current.removeAttachment(id);
    };
    flight.current = { sawWaiting: false };
    const sent = sessionId
      ? liveAttach(sessionId, wireOf(inFlight))
      : Promise.reject(new LineAttachRefused(0, null));
    sent.then(
      () => {
        // A line that ended meanwhile already emptied the card.
        if (!mine()) return;
        if (flight.current.sawWaiting && useLiveStore.getState().staged === 0)
          drop();
        else setPosted((p) => new Set([...p, ...ids]));
        setInFlight(null);
      },
      (err: unknown) => {
        if (!mine()) return;
        // Refused files never wait: their chips go, and the row says why.
        drop();
        setRefusal(
          err instanceof LineAttachRefused && err.words
            ? err.words
            : ATTACH_FAILED
        );
        setInFlight(null);
      }
    );
  }, [inFlight]);

  // A refusal holds the row for its beat, then the tray comes back.
  const note = open ? (refusal ?? lane.imageError ?? null) : null;
  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => {
      setRefusal(null);
      laneRef.current.setImageError(null);
    }, ATTACH_NOTE_MS);
    return () => clearTimeout(t);
  }, [note]);

  const removable = useCallback(
    (id: string) => !posted.has(id) && !inFlight?.some((r) => r.id === id),
    [posted, inFlight]
  );
  // The per-ask cap, before anything uploads: a pick that would take the
  // files past it is refused whole, in the door's own sentence. What is there
  // already is every row still in play on the card (uploading, on its way or
  // waiting on the line), or the server's own count when that is more.
  const fits = useCallback((n: number): boolean => {
    const rowsInPlay = laneRef.current.pendingAttachments.filter(
      (r) => r.status !== "error"
    ).length;
    const there = Math.max(rowsInPlay, useLiveStore.getState().staged);
    if (there + n <= LIVE_ATTACH_MAX_FILES) return true;
    setRefusal(LIVE_ATTACH_TOO_MANY);
    return false;
  }, []);
  const { openFilePicker, handleImageSelect, handleFiles, removeAttachment } =
    lane;
  const pick = useCallback(
    () =>
      openFilePicker((e) => {
        if (fits(e.target.files?.length ?? 0)) handleImageSelect(e);
      }),
    [openFilePicker, handleImageSelect, fits]
  );
  const stage = useCallback(
    (files: File[]) => {
      if (fits(files.length)) handleFiles(files);
    },
    [handleFiles, fits]
  );
  const remove = useCallback(
    (id: string) => {
      if (removable(id)) removeAttachment(id);
    },
    [removable, removeAttachment]
  );

  return { on, rows, removable, pick, stage, remove, note };
}
